// Aggregated `/accounts/{id}/bootstrap` wire contract (contract 07 §5). Ported 1:1 from v3
// `models/bootstrap.types.ts` (the 8-section aggregated wire shape); `rbac` (BE-ACC-01) made RBAC
// server-authoritative, and the BE-ACC-03/-05 nav sections (`feature_flags`, `reports`,
// `schedules`, `integrations_connected`, `dashboard_access`) let the sidebar hydrate from one
// boot call. `feature_flags` REQUIRES a `workspace_id` (backend `WORKSPACE_REQUIRED_SECTIONS`),
// so only workspace-scoped presets may request it; the other four are account/user-scoped.
import type { AccountDetail, AccountItem, UserAccessInfo, UserResponse } from './account.types'
// `RbacSection` is both USED below (the response envelope, the mapper's return) and re-exported
// for the consumers that imported it from here before the split — hence import + export, not a
// bare `export … from`, which would not bind the name locally.
import type { RbacSection } from './rbac.types'

// --- Section name union ---
/** Every section name 07 can REQUEST. The union below is derived from this array rather than
 *  written twice, so the runtime list and the type can never drift — `isBootstrapSection` needs
 *  the runtime form to narrow the server-supplied `DegradedSection.section` (deliberately a
 *  `string`, since the wire may name a section this client does not know). */
export const BOOTSTRAP_SECTIONS = [
  'account_core',
  'account_detail',
  'workspace_core',
  'dimensions',
  'compositions',
  'integrations',
  'settings',
  'session_access',
  'rbac',
  'feature_flags',
  'reports',
  'schedules',
  'integrations_connected',
  'dashboard_access',
] as const

export type BootstrapSection = (typeof BOOTSTRAP_SECTIONS)[number]

/** Narrow a wire-supplied section name to one this client can ask for again. An unknown name is
 *  not a bug — it is a section the server has and v4 does not model yet — but it must never reach
 *  a request, or the whole call 400s. */
export function isBootstrapSection(value: string): value is BootstrapSection {
  return (BOOTSTRAP_SECTIONS as readonly string[]).includes(value)
}

// --- Response envelope ---
/**
 * Per-section build outcome (BE-ACC-06), emitted for every REQUESTED section:
 *   • `ready`  — resolved with data → use the payload.
 *   • `empty`  — resolved but "you have none" (`[]` / `{"data": []}`) → an explicit empty value,
 *                NEVER an error.
 *   • `failed` — the build errored; payload `null`, same section rides `errors[]` with
 *                `code` + `retryable` → keep prior warm state.
 *   • `cold`   — requested but resolved from neither cache nor fresh build (defensive, backend
 *                calls it unexpected); payload `null`, NO `errors[]` entry → treated like
 *                `failed` (keep warm state), recorded retryable: a `refresh` re-ask is the remedy.
 * OPTIONAL as additive tolerance: pre-BE-ACC-06 payloads lack it — consumers then fall back to
 * null-based payload semantics (`null` ≈ failed/cold, present-but-empty = empty, else ready). */
export type BootstrapSectionStatus = 'ready' | 'empty' | 'failed' | 'cold'

export interface BootstrapMeta {
  account_id: number
  workspace_id: number | null
  generated_at: string
  section_versions: Partial<Record<BootstrapSection, number>>
  section_status?: Partial<Record<BootstrapSection, BootstrapSectionStatus>>
}

/** A per-section build failure riding the 200 body (BE-ACC-06). `code` is always the reserved
 *  `SECTION_FAILED`; `retryable` is the boot gate's "retry me" signal (transient upstream 5xx /
 *  unexpected → true, deterministic 4xx → false). `code`/`retryable` stay OPTIONAL as additive
 *  tolerance for pre-BE-ACC-06 payloads, which emitted a bare `{section, message}`. */
export interface BootstrapError {
  section: string
  message: string
  code?: string
  retryable?: boolean
}

/** A degraded-section record the mapper derives from `errors[]` + `meta.section_status` — the
 *  data-layer trace of a DEGRADED READY boot. `retryable` is normalised: absent on the wire
 *  (older payloads) or `cold` (no server verdict) → `true`, matching the backend's own
 *  "unknown → transient" classification. */
export interface DegradedSection {
  section: string
  status: 'failed' | 'cold'
  retryable: boolean
}

export interface BootstrapResponse {
  meta: BootstrapMeta
  errors: BootstrapError[]
  account_core?: AccountCoreSection | null
  account_detail?: AccountDetailSection | null
  workspace_core?: WorkspaceCoreSection | null
  dimensions?: DimensionsSection | null
  compositions?: CompositionsSection | null
  integrations?: IntegrationsSection | null
  settings?: SettingsSection | null
  session_access?: SessionAccessSection | null
  rbac?: RbacSection | null
  feature_flags?: FeatureFlagsSection | null
  reports?: ReportsSection | null
  schedules?: SchedulesSection | null
  integrations_connected?: IntegrationsConnectedSection | null
  dashboard_access?: DashboardAccessSection | null
}

// --- Section: account_core ---
export interface AccountCenterItem {
  id: number
  name: string
  url: string | null
  url_id: string
  sections: unknown[]
  account_id: number
}

export interface BootstrapWorkspaceItem {
  id: number
  label: string
  in_app_notifications: boolean
  shopify_shop_id: number | null
  timezone: string
  account_id: number
  created_at: string
  updated_at: string
  account: { id: number; user_id: number; [k: string]: unknown }
  account_permissions: string | unknown[]
  workspace_permissions: string | unknown[]
  is_workspace_admin: boolean
  is_ecommerce: boolean | null
  get_favorite_reports: unknown[]
  [k: string]: unknown
}

export interface BootstrapScheduleGroup {
  workspace_id: number
  schedules: BootstrapScheduleItem[]
}

export interface BootstrapScheduleItem {
  id: number
  workspace_id: number
  name: string | null
  report_id: number | null
  additional_info: Record<string, unknown> | null
  created_at: string
  updated_at: string
}

export interface AccountCoreSection {
  account_center: AccountCenterItem[]
  workspaces: BootstrapWorkspaceItem[]
  schedules: BootstrapScheduleGroup[]
}

// --- Section: account_detail ---
export type AccountDetailSection = AccountDetail

// --- Section: workspace_core ---
export interface PreferencesPayload {
  success: boolean
  data: Array<{ key: string; value: unknown; updated_at: string }>
  pagination: { total: number; limit: number; offset: number }
}

export interface TableViewConfigItem {
  table_id: string
  dimensions: string[]
  metrics: string[]
  sortField: string | null
  sortOrder: string | null
  breakdownColumn: string | null
  mediaColumns: string[] | null
  lineMetric: string | null
  checkLine: string | null
  activeChart: string | null
  groupColumn: string | null
}

export interface CustomDashboardItem {
  id: number | string
  name: string | null
  url: string | null
  url_id: string
  sections: unknown[] | null
  workspace_id: number
  version?: number | null
}

export interface CustomDashboardsPayload {
  data: CustomDashboardItem[]
  custom_dashboard_user: boolean
  workspace_attributes: Array<{ id: number; workspace: number; key: string; value: unknown }>
  workspace_integrations: string[]
}

export interface WorkspaceCoreSection {
  preferences: PreferencesPayload
  table_view_configs: TableViewConfigItem[]
  custom_dashboards: CustomDashboardsPayload
}

// --- Section: dimensions ---
export interface DimensionGroupItem {
  level: string
  name_key: string
  partition_name: string
  report_id: number
  data: {
    dimensions: string[]
    metrics: string[]
    primary_fields: string[] | null
  }
}

export interface DimensionsSection {
  table_columns_overview_all: {
    dimension_groups: Array<{
      workspace_id: number
      dimension_groups: DimensionGroupItem[]
    }>
  }
  dropdownable_dimensions: {
    data: {
      workspace_id: number
      dropdownable_dimensions: Record<string, Record<string, DropdownableSlice>>
    }
  }
}

/** `values` is absent in slim mode and present in full mode. */
export type DropdownableSlice = { values?: string[]; unique_count: number }

// --- Section: compositions ---
export interface CompositionRawItem {
  id: number
  workspace: number
  workspace_id: number
  name: string
  partition_name: string | null
  description: string | null
  config: { name: string; sources: unknown[]; description: string }
  sources: unknown[]
  compiled_config: Record<string, unknown>
  config_hash: string
  version: number
  is_active: boolean
  priority: number
  created_at: string
  updated_at: string
}

export interface CompositionsSection {
  data: CompositionRawItem[]
}

// --- Section: integrations ---
export interface IntegrationsSection {
  allowed_integrations: {
    data: {
      allowed_integrations: string[]
      integration_groups: Record<string, string[]>
    }
  }
  all_integrations: unknown[]
  connectable_integrations: {
    data: Record<string, boolean>
  }
}

// --- Section: settings ---
export interface SettingsSection {
  key_events: {
    data: Array<{
      id: number
      workspace_id: number
      key: string
      value: unknown
      created_at: string
      updated_at: string
    }>
    message: string
  }
}

// --- Section: session_access ---
export interface SessionAccessSection {
  user: UserResponse
  accessible_accounts: { data: AccountItem[] }
  user_access_info: UserAccessInfo
}

// --- Section: rbac (BE-ACC-01 / ORP-18806) — split out to fit `max-lines`; see `rbac.types.ts` ---
export type { CapabilityKey } from './rbac.types'
export type { RbacSection }

// --- Section: feature_flags (BE-ACC-02) ---
/** Enabled FeatureFlag keys for the request's `workspace_id`. Only `enabled_features` is emitted
 *  (`checkFlag()` tests membership only). REQUIRES a `workspace_id` — workspace presets only. */
export interface FeatureFlagsSection {
  enabled_features: string[]
}

// --- Section: reports (BE-ACC-03) ---
export interface ReportNavItem {
  id: number
  name: string
  /** The report's stable machine key (`Report.key`, BE-RPT-04); `null` when unset upstream. */
  key: string | null
  /** Nav-visible when `'Available'`. */
  status: string
}

export interface ReportsSection {
  data: ReportNavItem[]
}

// --- Section: schedules (BE-ACC-03) ---
/** Slim nav view over the SAME producer `account_core.schedules` embeds (report id +
 *  `additional_info` per schedule, grouped by workspace). Module 08 owns the consuming store (R3)
 *  — 07 only maps and dispatches this via the hydrator registry. */
export interface SchedulesSection {
  data: BootstrapScheduleGroup[]
}

// --- Section: integrations_connected (BE-ACC-03) ---
/** Connected integration names across the account's workspaces (one distinct query). */
export interface IntegrationsConnectedSection {
  data: string[]
}

// --- Section: dashboard_access (BE-ACC-05 / ORP-18808) ---
/** Every dashboard the caller can see across ALL visible workspaces, in one user-scoped payload —
 *  replaces the per-workspace `GET custom-dashboard/definitions/{ws}/` N+1 inside the boot flow.
 *  The two maps are keyed by stringified workspace id. */
export interface DashboardAccessSection {
  dashboards: CustomDashboardItem[]
  workspace_attributes: Record<string, unknown[]>
  workspace_integrations: Record<string, string[]>
}

// --- Mapped result (post-mapper, store-ready shapes) ---
export interface MappedBootstrapResult {
  // session_access
  user: UserResponse | null
  accounts: AccountItem[] | null
  userAccessInfo: UserAccessInfo | null
  // account_core
  accountCenters: unknown[] | null
  workspaces: unknown[] | null
  schedules: Record<string, unknown[]> | null
  // account_detail
  accountDetail: AccountDetail | null
  // workspace_core
  preferences: unknown[] | null
  tableViewConfigs: Record<string, unknown> | null
  dashboards: unknown[] | null
  customDashboardUser: boolean | null
  wsAttributes: Record<string, unknown> | null
  wsIntegrations: Record<string, unknown> | null
  // dimensions
  workspaceLevels: Record<string, unknown[]> | null
  dropdownables: Record<string, unknown> | null
  // compositions
  compositions: unknown[] | null
  // integrations
  allowedIntegrations: string[] | null
  integrationGroups: Record<string, unknown[]> | null
  allIntegrations: unknown[] | null
  connectableIntegrations: Record<string, boolean> | null
  // settings
  attributes: unknown[] | null
  // rbac
  rbac: RbacSection | null
  // feature_flags (workspace-scoped; → workspaceStore.setFeatureFlags)
  featureFlags: string[] | null
  // BE-ACC-03/-05 nav sections stay close to the wire for their registered hydrators.
  // `navSchedules` is the slim `schedules` section; `schedules` above is the account_core embed.
  reports: ReportNavItem[] | null
  navSchedules: BootstrapScheduleGroup[] | null
  integrationsConnected: string[] | null
  dashboardAccess: DashboardAccessSection | null
  // meta
  meta: BootstrapMeta
  errors: BootstrapError[]
  /** Normalised `meta.section_status`; `null` means use the pre-BE-ACC-06 payload semantics. */
  sectionStatus: Partial<Record<BootstrapSection, BootstrapSectionStatus>> | null
  /** Sections this response could not resolve (`failed`/`cold`) — the degraded-ready record. */
  degradedSections: DegradedSection[]
}
